import { useState, type ReactNode } from 'react';

import { Eye, EyeOff } from 'lucide-react';

import type { InputProps } from '@/components/ui/input';
import FormField from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
} from '@/components/ui/input-group';
import type { FormSpacing, InputWidth } from '@/theme/size-tokens';

export type FormInputProps = Omit<InputProps, 'validationState' | 'width'> & {
  /** Width of the input field - uses semantic sizing (handled by FormField) */
  width?: InputWidth;
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below input */
  helperText?: string;
  /** Spacing between label, input, and helper text */
  spacing?: FormSpacing;
  /** Error message to display */
  error?: string;
  /** Custom validation rule */
  pattern?: string;
  /** Custom validation message */
  title?: string;
  /** Input alignment */
  align?: 'left' | 'center' | 'right';
  /** Reserve space for error message to prevent layout shift */
  reserveMessageSpace?: boolean;
  /** Additional CSS classes applied to the label element */
  labelClassName?: string;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
  /**
   * When true and type="password", renders a persistent eye toggle button
   * so the user can reveal or hide the value at any time.
   */
  showPasswordToggle?: boolean;
};

type PasswordGroupProps = InputProps & {
  visible: boolean;
  onToggle: () => void;
};

/**
 * The reveal-toggle variant. FormField clones `id`, `validationState` and
 * `aria-describedby` onto its direct child, so this has to be that child and
 * hand them to the real input; cloned onto the InputGroup they land on a div,
 * and the label points at something that cannot be labelled.
 */
function PasswordGroup({
  visible,
  onToggle,
  disabled,
  className: _ignored,
  ...inputProps
}: PasswordGroupProps) {
  return (
    <InputGroup className="dark:bg-transparent">
      <Input
        {...inputProps}
        disabled={disabled}
        data-slot="input-group-control"
        className="flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent"
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          size="icon-xs"
          aria-label={visible ? 'Hide value' : 'Show value'}
          onClick={onToggle}
          disabled={disabled}
        >
          {visible ? <EyeOff /> : <Eye />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

/**
 * FormInput - Input wrapped with FormField for label and error display
 *
 * Uses native HTML5 validation. Set `pattern` and `title` for custom validation messages.
 *
 * **Validation State:**
 * - When `error` prop is provided, the validation state is set to 'error', the input displays
 *   error styling (red border), and the error message is shown below the input
 * - The `error` prop takes precedence over `helperText` - only one is displayed at a time
 * - Without an error, `helperText` is displayed in default styling
 *
 * @example
 * ```tsx
 * // Simple required email
 * <FormInput label="Email" type="email" required placeholder="you@example.com" />
 *
 * // With error state
 * <FormInput label="Email" error="Invalid email address" helperText="We'll never share" />
 *
 * // With custom validation message
 * <FormInput
 *   label="Corporate Email"
 *   type="email"
 *   required
 *   pattern=".+@company\.com"
 *   title="Please use your company email address"
 *   placeholder="you@company.com"
 * />
 *
 * // Number with range
 * <FormInput label="Age" type="number" min={18} max={120} required />
 *
 * // Password with reveal toggle
 * <FormInput label="API Key" type="password" showPasswordToggle required />
 *
 * ```
 */
function FormInput({
  // FormField props
  label,
  helperText,
  spacing,
  error,
  required,
  disabled,
  id,
  pattern,
  title,
  width,
  align = 'center',
  reserveMessageSpace = false,
  labelClassName,
  labelSuffix,
  onChange,
  showPasswordToggle = false,
  ...inputProps
}: Readonly<FormInputProps>) {
  const [visible, setVisible] = useState(false);

  const effectiveType =
    showPasswordToggle && inputProps.type === 'password' && visible
      ? 'text'
      : inputProps.type;

  const inputElement = (
    <Input
      {...inputProps}
      type={effectiveType}
      onChange={onChange}
      required={required}
      disabled={disabled}
      id={id}
      pattern={pattern}
      title={title}
    />
  );

  return (
    <FormField
      label={label}
      helperText={helperText}
      spacing={spacing}
      error={error}
      required={required}
      disabled={disabled}
      id={id}
      width={width}
      align={align}
      reserveMessageSpace={reserveMessageSpace}
      labelClassName={labelClassName}
      labelSuffix={labelSuffix}
    >
      {showPasswordToggle ? (
        <PasswordGroup
          {...inputProps}
          type={effectiveType}
          onChange={onChange}
          required={required}
          disabled={disabled}
          id={id}
          pattern={pattern}
          title={title}
          visible={visible}
          onToggle={() => setVisible((v) => !v)}
        />
      ) : (
        inputElement
      )}
    </FormField>
  );
}

FormInput.displayName = 'FormInput';

export default FormInput;
